import { DISPLAY_STYLES } from '../edit-transaction/format-field-value'
import {
  DisplayContainer,
  DisplayPresent,
  DisplayStyle,
  listDisplayKeys,
  TransactionFieldMetadata,
  TransactionListValues,
  TransactionMetadataConfig,
} from '../models/transaction-metadata.model'

/**
 * Adapts the metadata document to the model the modal reads.
 *
 * The document is the source of truth for the CONTENT; this file is the one place its spelling is
 * reconciled with the model's. It exists because the two were authored by different tiers and
 * disagree in five mechanical ways — none of them a disagreement about meaning, all of them enough
 * to render an empty dialog if left unreconciled.
 *
 * Keeping the reconciliation here rather than in the model has one concrete payoff: the static
 * document and the flex-transactions endpoint are the SAME shape, so both sources adapt through this
 * function and the projection, the service and every consumer stay unaware there was ever a
 * difference.
 */

/** A field as the document spells it: widened primitives, flattened container, string `order`. */
interface RawField {
  [key: string]: unknown
  field: string
  order: string | number
  displayContainer: string
  settings?: {
    [key: string]: unknown
    grouping?: { [key: string]: unknown; logic?: string; parameters?: string }
    requiredParameters?: string | number
  }
  comparison?: {
    prometheusCustodyKey?: string
    prometheusTradingKey?: string
    clearwaterKey?: string
  }
}

interface RawConfig {
  tranCategories: unknown[]
  fields: RawField[]
}

/**
 * The document flattens the design's five Display Containers into one column; the model splits them
 * back into the `displayContainer` × `present` pair its own KDoc tabulates. Only two of the five
 * carry a presentation, which is why only two appear here.
 */
const CONTAINER_PAIRS: Readonly<
  Record<string, { container: DisplayContainer; present?: DisplayPresent }>
> = {
  footerField: { container: 'default', present: 'footerField' },
  signedSplit: { container: 'arrayField', present: 'signedSplit' },
}

/**
 * The collection a field belongs to, derived from its own name.
 *
 * The document does not carry `collection`; it does not need to, because the relationship is already
 * in the key. A summary IS its collection (`distributionLines`), and a column is the prefix of its
 * dotted name (`distributionLines.date` -> `distributionLines`). Verified against the whole
 * document: every one of the 103 `arrayField` fields has a prefix that is an `arraySummary` field,
 * with no exceptions, so there is no case where this guesses.
 *
 * Without this the projection matches no column to any summary — `undefined === 'distributionLines'`
 * is never true — and a collection renders as a summary row with an empty items grid and an
 * item-count aggregate instead of a sum. Silently: nothing throws.
 */
const collectionOf = (field: string, container: string): string | undefined => {
  if (container === 'arraySummary') return field
  if (container === 'arrayField' || container === 'signedSplit') {
    const prefix = field.split('.')[0]
    return prefix === field ? undefined : prefix
  }
  return undefined
}

/**
 * A Prometheus comparison key without its synthetic path prefix.
 *
 * The document spells these as `aggregateInfo.Trade_Date`, naming the blob the value happens to
 * arrive in. The model wants the key alone: the projection already knows which blob to read, and a
 * path baked into the key stops it matching the sample column set. Only the prefix goes — the key
 * itself is untouched.
 */
/** The two spellings of each comparison key, in one place. */
export const COMPARISON_KEYS = {
  cust: { documentKey: 'prometheusCustodyKey', modelKey: 'custodyKey' },
  trad: { documentKey: 'prometheusTradingKey', modelKey: 'tradingKey' },
  cw: { documentKey: 'clearwaterKey', modelKey: 'clearwaterKey' },
} as const

export type ComparisonOrigin = keyof typeof COMPARISON_KEYS

/** The comparison origins in the design's own column order. */
export const COMPARISON_ORIGINS: readonly ComparisonOrigin[] = [
  'cust',
  'trad',
  'cw',
]

/** A document key name as a column header: `prometheusCustodyKey` -> `Prometheus Custody`. */
export const comparisonKeyLabel = (documentKey: string): string =>
  documentKey
    .replace(/Key$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, first => first.toUpperCase())

const AGGREGATE_PREFIX = 'aggregateInfo.'

const bareKey = (key?: string): string | undefined =>
  key?.startsWith(AGGREGATE_PREFIX) ? key.slice(AGGREGATE_PREFIX.length) : key

/** Styles already reported, so a document that is adapted twice says each one once. */
const warnedDisplayStyles = new Set<string>()

/**
 * A field's Display Style, or `default` when the document names one this UI has no rule for.
 *
 * The document is served live, so `displayStyle` is data rather than a compile-time promise: a
 * style `DISPLAY_STYLES` has no entry for, or none at all, would make `formatFieldValue` throw and
 * keep every dialog from opening. `default` shows the value as the source sent it.
 *
 * An own-property test, not `in`: `'toString' in DISPLAY_STYLES` is true.
 */
const displayStyleOf = (field: string, style: unknown): DisplayStyle => {
  if (
    typeof style === 'string' &&
    Object.prototype.hasOwnProperty.call(DISPLAY_STYLES, style)
  ) {
    return style as DisplayStyle
  }

  const key = `${field}:${String(style)}`
  if (!warnedDisplayStyles.has(key)) {
    warnedDisplayStyles.add(key)
    console.error(
      `[transactions-2] field "${field}" names display style "${String(
        style,
      )}", which this UI cannot format. Using "default". Known styles: ${Object.keys(
        DISPLAY_STYLES,
      ).join(', ')}.`,
    )
  }
  return 'default'
}

const adaptField = (raw: RawField): TransactionFieldMetadata => {
  const pair = CONTAINER_PAIRS[raw.displayContainer]
  const settings = raw.settings ?? {}
  const grouping = settings.grouping

  const adapted: Record<string, unknown> = {
    ...raw,
    // `20.1` -> 20.1. Lossless for everything the modal renders: all 32 `shown` fields hold a
    // distinct number. The seven colliding pairs the string form protects (`20.1`/`20.10`,
    // `20.2`/`20.20`, 68, 69, 78, 153, 154) are ALL between hidden fields, which is why this
    // conversion is safe today and why `guardsDistinctShownOrders` exists to catch the day it is not.
    order: Number(raw.order),
    displayContainer: pair?.container ?? raw.displayContainer,
    displayStyle: displayStyleOf(raw.field, raw.displayStyle),
  }

  if (pair?.present) adapted.present = pair.present

  // PVAL-26685: every list leaves here carrying its three response keys. RPS serves them on every
  // lookup it has migrated; an older document (or an un-keyed lookup) gets the static defaults,
  // so no consumer ever has to ask whether they are there. `derivationInputs` needs nothing — the
  // spread above carries it through as served.
  const listKeys = listDisplayKeys(
    raw.listValues as TransactionListValues | undefined,
  )
  if (listKeys && raw.listValues && typeof raw.listValues === 'object') {
    adapted.listValues = { ...raw.listValues, ...listKeys }
  }

  const collection = collectionOf(raw.field, raw.displayContainer)
  if (collection) adapted.collection = collection

  if (grouping) {
    const rule: Record<string, unknown> = { ...grouping }
    // The document names the condition `parameters`; the model names it `conditionField`.
    if (grouping.parameters) rule.conditionField = grouping.parameters
    delete rule.parameters
    adapted.settings = { ...settings, grouping: rule }
  }

  const adaptedSettings = (adapted.settings ?? settings) as Record<
    string,
    unknown
  >
  if (settings.requiredParameters != null) {
    // Plural and a string in the document; singular and a count in the model.
    const next = { ...adaptedSettings }
    next.requiredParameter = Number(settings.requiredParameters)
    delete next.requiredParameters
    adapted.settings = next
  }

  if (raw.comparison) {
    adapted.comparison = {
      custodyKey: bareKey(raw.comparison[COMPARISON_KEYS.cust.documentKey]),
      tradingKey: bareKey(raw.comparison[COMPARISON_KEYS.trad.documentKey]),
      clearwaterKey: raw.comparison[COMPARISON_KEYS.cw.documentKey],
    }
  }

  // `Debit|Credit` names both halves — the model's own DisplayPresent KDoc says so. The document
  // leaves `split` null, so the labels come from the label rather than being invented here.
  if (pair?.present === 'signedSplit') {
    const [debitLabel, creditLabel] = String(raw.label ?? '').split('|')
    if (debitLabel && creditLabel) {
      adapted.split = {
        debitLabel: debitLabel.trim(),
        creditLabel: creditLabel.trim(),
      }
    }
  }

  return adapted as unknown as TransactionFieldMetadata
}

/**
 * Adapts a whole document. Both {@link StaticTransactionMetadataService} and
 * {@link HttpTransactionMetadataService} go through here, so a field behaves identically whether it
 * was read from disk or from flex-transactions.
 */
export const adaptTransactionMetadata = (
  raw: unknown,
): TransactionMetadataConfig => {
  const document = raw as RawConfig
  return {
    ...(document as unknown as TransactionMetadataConfig),
    fields: document.fields.map(adaptField),
  }
}
